import { MIN_STEP_FALLBACK_MS } from '@/core';
import { colorLookup, HeatmapCanvasColorOptions } from '../HeatMapCanvas';
import { HeatMapChartData } from '../heat-map-model';
import { LOG_EPSILON } from './axis';

/**
 * Render heatmap data to an offscreen canvas using value-based Y positioning.
 * @param colorScaleMax  The maximum count value used for color normalization.
 *   Kept stable across Y-zoom so cell colors match the visualMap legend.
 */
export function drawHeatmapToCanvas(
  canvas: HTMLCanvasElement,
  data: HeatMapChartData,
  width: number,
  height: number,
  colorOptions: HeatmapCanvasColorOptions,
  minValue: number,
  maxValue: number,
  scaleType: 'value' | 'log',
  startTimeMs: number,
  endTimeMs: number,
  colorScaleMax: number
): void {
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const valueRange = maxValue - minValue;
  if (!Number.isFinite(valueRange) || valueRange <= 0) return;

  const clampNonFinite = (val: number, fallback: number) => (Number.isFinite(val) ? val : fallback);

  if (colorOptions.hideZeros) {
    ctx.clearRect(0, 0, width, height);
  } else {
    const zeroColor = colorLookup(0, colorScaleMax, colorOptions);
    ctx.fillStyle = `rgb(${zeroColor[0]},${zeroColor[1]},${zeroColor[2]})`;
    ctx.fillRect(0, 0, width, height);
  }

  const stepMs =
    typeof data.timeRange.stepMs === 'number' && data.timeRange.stepMs > 0
      ? data.timeRange.stepMs
      : MIN_STEP_FALLBACK_MS;

  const valueToY = (value: number) => {
    if (scaleType === 'log') {
      const safeMin = Math.max(LOG_EPSILON, minValue);
      const safeMax = Math.max(LOG_EPSILON, maxValue);
      const safeVal = Math.max(LOG_EPSILON, value);
      const logRange = Math.log10(safeMax) - Math.log10(safeMin);
      if (!Number.isFinite(logRange) || logRange <= 0) return height;
      const fraction = (Math.log10(safeVal) - Math.log10(safeMin)) / logRange;
      return Math.round(height * (1 - fraction));
    }
    return Math.round(height * (1 - (value - minValue) / valueRange));
  };

  for (let i = 0; i < data.timeSlices.length; i++) {
    const timeSlice = data.timeSlices[i];
    if (!timeSlice) continue;

    const colStartMs = timeSlice.timestamp;
    if (colStartMs >= endTimeMs) continue;
    const nextSlice = data.timeSlices[i + 1];
    const colEndMs = nextSlice ? Math.min(nextSlice.timestamp, colStartMs + stepMs) : colStartMs + stepMs;
    if (colEndMs <= startTimeMs) continue;

    const visStart = Math.max(colStartMs, startTimeMs);
    const visEnd = Math.min(colEndMs, endTimeMs);
    const xStart = Math.round(((visStart - startTimeMs) / (endTimeMs - startTimeMs)) * width);
    const xEnd = Math.round(((visEnd - startTimeMs) / (endTimeMs - startTimeMs)) * width);

    for (const dp of timeSlice.dataPoints) {
      if (!dp || dp.value < 0) continue;
      const startB = data.buckets[dp.bucketIndex];
      const endB = data.buckets[dp.bucketIndex + dp.bucketSpan - 1];
      if (!startB || !endB) continue;
      const lower = clampNonFinite(startB.lower, minValue);
      const upper = clampNonFinite(endB.upper, maxValue);
      if (lower > maxValue || upper < minValue) continue;

      const valPerBucket = dp.value / dp.bucketSpan;
      if (colorOptions.hideZeros && valPerBucket === 0) continue;

      const yTop = valueToY(upper);
      const yBot = valueToY(lower);
      const color = colorLookup(valPerBucket, colorScaleMax, colorOptions);
      ctx.fillStyle = `rgb(${color[0]},${color[1]},${color[2]})`;
      ctx.fillRect(xStart, yTop, xEnd - xStart, yBot - yTop);
    }
  }
}
